import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable, faTh, faThList } from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Button, ButtonGroup, Col, Container, Row } from "reactstrap";
import { HISTORY_POST_LOADING } from "../../constants/loading.constant";
import { useGlobalCore } from "../../cores/globals";
import { useRouterCore } from "../../cores/router";
import { useSocialPostCore } from "../../cores/socialPost";
import { NoData } from "../component/NoData";
import { SocialHistoryBar } from "../component/socialHistory/SocialHistoryBar";
import { SocialHistoryCard } from "../component/socialHistory/SocialHistoryCard";
import { SocialHistoryTable } from "../component/socialHistory/SocialHistoryTable";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import { useIntl } from "react-intl";
import { useMeCore } from "../../cores/me";
import "./SocialHistory.scss";

type HistoryViewMode = "cards" | "grid" | "table";

export default function SocialHistory() {
  const intl = useIntl();
  const { socialPosts } = useSocialPostCore();
  const { isDarkMode } = useGlobalCore();
  const { goToSocialPost } = useRouterCore();
  const { companyRole, isAdmin, isReseller } = useMeCore();
  const { goToSocial } = useRouterCore();

  /** Siempre grilla al entrar; el usuario puede cambiar solo en esta sesión. */
  const [viewMode, setViewMode] = useState<HistoryViewMode>("grid");

  const sortedPosts = useMemo(() => {
    return [...socialPosts].sort((a, b) => {
      const ma = moment(a.post.postAt || a.post.scheduleDate);
      const mb = moment(b.post.postAt || b.post.scheduleDate);
      const va = ma.isValid() ? ma.valueOf() : 0;
      const vb = mb.isValid() ? mb.valueOf() : 0;
      return vb - va;
    });
  }, [socialPosts]);

  useEffect(() => {
    if (
      !companyRole?.social &&
      !companyRole?.owner &&
      !companyRole?.admin &&
      !companyRole?.agent &&
      !isAdmin &&
      !isReseller
    )
      goToSocial();
  }, [goToSocial, companyRole, isAdmin, isReseller]);

  return (
    <Layout>
      <Header2 title={intl.formatMessage({ id: "app.sidebar.socialMediaRecord" })} />
      <Content>
        <Container fluid>
          <Row>
            <Col xs="12">
              <SocialHistoryBar />
              <UpdateLoadingCard
                loadingId={HISTORY_POST_LOADING}
                text={intl.formatMessage({ id: "app.social.updating" })}
              />
              <div
                className={cn("social-history-page", {
                  "social-history-page--dark": isDarkMode,
                })}
              >
                <div className="social-history-page__intro">
                  <div className="social-history-page__title-row">
                    <p className="social-history-page__subtitle mb-0">
                      {intl.formatMessage({ id: "app.socialHistory.subtitle" })}
                    </p>
                    <div className="social-history-page__toolbar">
                      {sortedPosts.length > 0 && (
                        <>
                          <span className="social-history-page__stats">
                            {intl.formatMessage(
                              { id: "app.socialHistory.itemsCount" },
                              { count: sortedPosts.length }
                            )}
                          </span>
                          <ButtonGroup
                            className="social-history-page__view-toggle"
                            aria-label={intl.formatMessage({
                              id: "app.socialHistory.viewModeLabel",
                            })}
                          >
                            <Button
                              type="button"
                              outline
                              color="secondary"
                              size="sm"
                              active={viewMode === "cards"}
                              aria-pressed={viewMode === "cards"}
                              onClick={() => setViewMode("cards")}
                              title={intl.formatMessage({
                                id: "app.socialHistory.viewCards",
                              })}
                            >
                              <FontAwesomeIcon icon={faThList} className="mr-1" />
                              <span className="d-none d-sm-inline">
                                {intl.formatMessage({ id: "app.socialHistory.viewCards" })}
                              </span>
                            </Button>
                            <Button
                              type="button"
                              outline
                              color="secondary"
                              size="sm"
                              active={viewMode === "grid"}
                              aria-pressed={viewMode === "grid"}
                              onClick={() => setViewMode("grid")}
                              title={intl.formatMessage({
                                id: "app.socialHistory.viewGrid",
                              })}
                            >
                              <FontAwesomeIcon icon={faTh} className="mr-1" />
                              <span className="d-none d-sm-inline">
                                {intl.formatMessage({ id: "app.socialHistory.viewGrid" })}
                              </span>
                            </Button>
                            <Button
                              type="button"
                              outline
                              color="secondary"
                              size="sm"
                              active={viewMode === "table"}
                              aria-pressed={viewMode === "table"}
                              onClick={() => setViewMode("table")}
                              title={intl.formatMessage({
                                id: "app.socialHistory.viewTable",
                              })}
                            >
                              <FontAwesomeIcon icon={faTable} className="mr-1" />
                              <span className="d-none d-sm-inline">
                                {intl.formatMessage({ id: "app.socialHistory.viewTable" })}
                              </span>
                            </Button>
                          </ButtonGroup>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className={cn("social-history-page__list", {
                    "social-history-page__list--cards": viewMode === "cards",
                    "social-history-page__list--grid": viewMode === "grid",
                    "social-history-page__list--table": viewMode === "table",
                  })}
                >
                  {viewMode === "table" ? (
                    sortedPosts.length > 0 ? (
                      <SocialHistoryTable posts={sortedPosts} isDarkMode={isDarkMode} />
                    ) : null
                  ) : (
                    sortedPosts.map((post) => (
                      <SocialHistoryCard
                        key={post.post.id}
                        post={post}
                        isDarkMode={isDarkMode}
                        variant={viewMode === "grid" ? "compact" : "full"}
                      />
                    ))
                  )}
                  {sortedPosts.length === 0 && (
                    <div className="social-history-page__empty">
                      <NoData
                        className="social-history-page__empty-row"
                        image="generic5"
                        text={intl.formatMessage({ id: "app.social.noPost" })}
                        actionLabel={intl.formatMessage({ id: "app.social.post" })}
                        action={() => goToSocialPost("")}
                        horizontal
                      />
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </Content>
    </Layout>
  );
}
